import { MapPin, SlidersHorizontal, Search, Heart, TreePine } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CityOption } from "@/hooks/useExploreLocation";
import { DALARNA_CITIES } from "@/hooks/useExploreLocation";

/**
 * Farmly-toned empty states for Explore.
 * See docs/design-rules/ux-rules/explore-12-10-sot.md §9
 *
 * "Det verkar lugnt här just nu." — poetiskt, handlingsbart.
 */

interface ExploreEmptyStateProps {
  variant: "no-results" | "near-me" | "search" | "favorites" | "filtered";
  onClearFilters?: () => void;
  onClearSearch?: () => void;
  onClearNearMe?: () => void;
  onSelectCity?: (city: CityOption) => void;
  onToggleFav?: () => void;
  searchQuery?: string;
  modeLabel?: string;
}

export function ExploreEmptyState({
  variant,
  onClearFilters,
  onClearSearch,
  onClearNearMe,
  onSelectCity,
  onToggleFav,
  searchQuery,
  modeLabel = "platser",
}: ExploreEmptyStateProps) {
  if (variant === "favorites") {
    return (
      <div className="py-16 px-8 text-center">
        <Heart className="h-10 w-10 mx-auto mb-4 text-muted-foreground/30" />
        <p className="text-base font-medium text-foreground mb-1">
          Inga sparade {modeLabel} ännu
        </p>
        <p className="text-sm text-muted-foreground mb-5">
          Utforska och spara det som inspirerar dig.
        </p>
        {onToggleFav && (
          <Button variant="outline" size="sm" onClick={onToggleFav}>
            Utforska {modeLabel}
          </Button>
        )}
      </div>
    );
  }

  if (variant === "search") {
    return (
      <div className="py-16 px-8 text-center">
        <Search className="h-10 w-10 mx-auto mb-4 text-muted-foreground/30" />
        <p className="text-base font-medium text-foreground mb-1">
          Inga resultat för "{searchQuery}"
        </p>
        <p className="text-sm text-muted-foreground mb-5">
          Prova ett annat sökord eller rensa sökningen.
        </p>
        {onClearSearch && (
          <Button variant="outline" size="sm" onClick={onClearSearch}>
            Rensa sökning
          </Button>
        )}
      </div>
    );
  }

  if (variant === "near-me") {
    return (
      <div className="py-16 px-8 text-center">
        <MapPin className="h-10 w-10 mx-auto mb-4 text-muted-foreground/30" />
        <p className="text-base font-medium text-foreground mb-1">
          Det verkar lugnt här just nu
        </p>
        <p className="text-sm text-muted-foreground mb-5">
          Vill du bredda området eller välja en annan plats?
        </p>
        <div className="flex flex-col items-center gap-3">
          {onClearNearMe && (
            <Button variant="outline" size="sm" onClick={onClearNearMe}>
              Visa alla
            </Button>
          )}
          {onSelectCity && (
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {DALARNA_CITIES.slice(0, 4).map((city) => (
                <Button
                  key={city.name}
                  variant="ghost"
                  size="sm"
                  className="text-xs h-8"
                  onClick={() => onSelectCity(city)}
                >
                  {city.name}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (variant === "filtered") {
    return (
      <div className="py-16 px-8 text-center">
        <SlidersHorizontal className="h-10 w-10 mx-auto mb-4 text-muted-foreground/30" />
        <p className="text-base font-medium text-foreground mb-1">
          Det verkar lugnt här just nu
        </p>
        <p className="text-sm text-muted-foreground mb-5">
          Rensa filter eller välj en annan plats.
        </p>
        {onClearFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            Rensa filter
          </Button>
        )}
      </div>
    );
  }

  // Default: no-results
  const isActivities = modeLabel === "aktiviteter";
  return (
    <div className="py-16 px-8 text-center">
      <TreePine className="h-10 w-10 mx-auto mb-4 text-muted-foreground/30" />
      <p className="text-base font-medium text-foreground mb-1">
        Det verkar lugnt här just nu.
      </p>
      <p className="text-sm text-muted-foreground">
        {isActivities
          ? "Kom tillbaka snart — nya upplevelser dyker upp hela tiden."
          : "Platser, gårdar och upplevelser värda att åka till — snart."}
      </p>
    </div>
  );
}
